import { twoFactorClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { MessageKey } from "../i18n/messages";
import { disablePush } from "../lib/push";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /** Avatar URL (uploaded in Mi cuenta, or Google's picture). */
  image?: string | null;
}

/** twoFactor: the password was right; the sign-in waits for the authenticator code (verifyTwoFactor). */
export type AuthResult = { ok: true } | { ok: false; error: MessageKey; twoFactor?: true };

export interface TwoFactorSetup {
  totpURI: string;
  backupCodes: string[];
}

export interface AuthValue {
  user: AuthUser | null;
  loading: boolean;
  googleEnabled: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  /** marketingOptIn: the unticked-by-default "send me offers" checkbox (explicit consent). */
  signUp: (name: string, email: string, password: string, next: string, marketingOptIn?: boolean) => Promise<AuthResult>;
  /** Re-reads the session (after editing the profile or the avatar). */
  refresh: () => Promise<void>;
  signInWithGoogle: (next: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  resetPassword: (token: string, password: string) => Promise<AuthResult>;
  resendVerification: (email: string, next: string) => Promise<AuthResult>;
  /** Second step of a password sign-in with 2FA: an authenticator code or a backup code. */
  verifyTwoFactor: (code: string, opts: { backup?: boolean; trustDevice?: boolean }) => Promise<AuthResult>;
  /** Starts 2FA enrollment (password required when the account has one); confirm with verifyTwoFactor. */
  enableTwoFactor: (password: string) => Promise<{ ok: true; setup: TwoFactorSetup } | { ok: false; error: MessageKey }>;
}

interface ClientError {
  code?: string;
  status?: number;
  message?: string;
}

/** Maps Better Auth error codes/statuses to UI message keys. */
export function authErrorKey(error: ClientError | null | undefined): MessageKey {
  const code = (error?.code ?? "").toUpperCase();
  if (error?.status === 429) return "auth.errors.tooMany";
  // "Próximamente": only invited emails (beta testers, admins) can sign up or sign in.
  if (error?.status === 503 || code.includes("COMING_SOON")) return "auth.errors.invitesOnly";
  if (code.includes("EMAIL_NOT_VERIFIED") || (error?.status === 403 && !code)) return "auth.errors.notVerified";
  if (code.includes("INVALID_EMAIL_OR_PASSWORD") || code.includes("INVALID_PASSWORD") || error?.status === 401) return "auth.errors.invalidCredentials";
  if (code.includes("USER_ALREADY_EXISTS")) return "auth.errors.userExists";
  if (code.includes("INVALID_TOKEN") || code.includes("EXPIRED")) return "auth.errors.invalidToken";
  if (code.includes("PASSWORD_TOO_SHORT")) return "auth.errors.password";
  if (code.includes("INVALID_EMAIL")) return "auth.errors.email";
  return "auth.errors.generic";
}

/** Only same-site relative paths are allowed as post-login destinations (prevents open redirects). */
export function safeNext(next: string | null | undefined, fallback = "/cuenta"): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return fallback;
  return next;
}

const AuthContext = createContext<AuthValue | null>(null);

function wrap(run: () => Promise<{ error: ClientError | null }>): Promise<AuthResult> {
  return run()
    .then(({ error }) => (error ? { ok: false as const, error: authErrorKey(error) } : { ok: true as const }))
    .catch(() => ({ ok: false as const, error: "auth.errors.generic" as MessageKey }));
}

/** Real implementation backed by Better Auth's React client (same origin, /api/auth). */
function BetterAuthProvider({ children }: { children: ReactNode }) {
  // onTwoFactorRedirect: no navigation — the sign-in forms show the code step themselves.
  const client = useMemo(() => createAuthClient({ basePath: "/api/auth", plugins: [twoFactorClient({ onTwoFactorRedirect: () => {} })] }), []);
  const session = client.useSession();
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    fetch("/api/auth/providers")
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => setGoogleEnabled(Boolean((body as { data?: { google?: boolean } } | null)?.data?.google)))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const value = useMemo<AuthValue>(() => {
    const u = session.data?.user;
    return {
      user: u ? { id: u.id, name: u.name, email: u.email, image: u.image ?? null } : null,
      loading: session.isPending,
      googleEnabled,
      signIn: async (email, password) => {
        try {
          const { data, error } = await client.signIn.email({ email, password });
          if (error) return { ok: false, error: authErrorKey(error) };
          if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) return { ok: false, error: "auth.twoFactor.needed", twoFactor: true };
          return { ok: true };
        } catch {
          return { ok: false, error: "auth.errors.generic" };
        }
      },
      verifyTwoFactor: async (code, { backup = false, trustDevice = false }) => {
        const r = await wrap(() => (backup ? client.twoFactor.verifyBackupCode({ code, trustDevice }) : client.twoFactor.verifyTotp({ code, trustDevice })));
        return r.ok ? r : { ok: false, error: "auth.twoFactor.invalid" };
      },
      enableTwoFactor: async (password) => {
        try {
          const { data, error } = await client.twoFactor.enable(password ? { password } : ({} as { password: string }));
          if (error || !data || !("totpURI" in data) || !data.totpURI) return { ok: false, error: error ? authErrorKey(error) : "auth.errors.generic" };
          return { ok: true, setup: { totpURI: data.totpURI, backupCodes: data.backupCodes ?? [] } };
        } catch {
          return { ok: false, error: "auth.errors.generic" };
        }
      },
      signUp: (name, email, password, next, marketingOptIn = false) =>
        wrap(() => client.signUp.email({ name, email, password, callbackURL: safeNext(next), marketingOptIn } as Parameters<typeof client.signUp.email>[0])),
      refresh: async () => {
        await session.refetch();
      },
      signInWithGoogle: (next) => wrap(() => client.signIn.social({ provider: "google", callbackURL: safeNext(next), errorCallbackURL: "/ingresar" })),
      signOut: async () => {
        // This device stops getting the account's notifications (while the session still exists).
        await disablePush().catch(() => undefined);
        await client.signOut().catch(() => undefined);
      },
      requestPasswordReset: (email) => wrap(() => client.requestPasswordReset({ email, redirectTo: "/restablecer" })),
      resetPassword: (token, password) => wrap(() => client.resetPassword({ token, newPassword: password })),
      resendVerification: (email, next) => wrap(() => client.sendVerificationEmail({ email, callbackURL: safeNext(next) })),
    };
  }, [client, session.data, session.isPending, googleEnabled]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Pass `value` in tests to avoid network calls. */
export function AuthProvider({ children, value }: { children: ReactNode; value?: AuthValue }) {
  if (value) return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  return <BetterAuthProvider>{children}</BetterAuthProvider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
